import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Award,
  Building2,
  Calendar,
  Hash,
  ChevronRight,
  X,
  Sparkles,
  ShieldCheck,
  GraduationCap,
  FlaskConical,
  BarChart3,
  Hammer,
  Crown,
  BrainCircuit,
} from "lucide-react";
import { CREDENTIALS } from "./data";
import { useContent } from "./useContent";
import { resolveFileUrl } from "./fileUrl";

const CATEGORY_META = {
  Capital:    { Icon: BarChart3,    color: "#e6c769" },
  Leadership: { Icon: Crown,        color: "#86efac" },
  Frontier:   { Icon: BrainCircuit, color: "#f0abfc" },
  Build:      { Icon: Hammer,       color: "#fbbf24" },
  Research:   { Icon: FlaskConical, color: "#c4b5fd" },
  Academic:   { Icon: GraduationCap,color: "#a5b4fc" },
};

const FILTERS = ["All", "Capital", "Build", "Leadership", "Research", "Academic", "Frontier"];

const CredentialRow = ({ c, onClick, index }) => {
  const meta = CATEGORY_META[c.category] || { Icon: Award, color: "#fff" };
  const { Icon, color } = meta;

  return (
    <motion.button
      onClick={onClick}
      initial={{ opacity: 0, y: 26 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-40px" }}
      transition={{ duration: 0.6, delay: Math.min(index * 0.04, 0.4), ease: [0.16, 1, 0.3, 1] }}
      data-testid={`credential-card-${c.id}`}
      className="group relative w-full text-left rounded-[22px] overflow-hidden border border-white/10 bg-gradient-to-br from-white/[0.05] to-white/[0.01] backdrop-blur-xl hover:border-white/25 transition-all hover:-translate-y-1"
    >
      <div
        className="absolute -top-20 -right-20 w-56 h-56 rounded-full opacity-30 group-hover:opacity-60 transition-opacity blur-3xl"
        style={{ background: `${color}33` }}
      />

      <div className="relative p-6 md:p-7">
        {/* Top row: badge + year */}
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-center gap-3">
            <div
              className="w-12 h-12 rounded-xl grid place-items-center border backdrop-blur-md shrink-0"
              style={{ background: `${color}14`, borderColor: `${color}40`, color }}
            >
              <Icon className="w-5 h-5" />
            </div>
            <div>
              <span
                className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] tracking-[0.25em] uppercase font-mono border"
                style={{ borderColor: `${color}55`, color, background: `${color}10` }}
              >
                {c.category}
              </span>
            </div>
          </div>
          <div className="text-right">
            <div className="text-[10px] tracking-[0.3em] uppercase text-white/40 font-mono">Issued</div>
            <div className="text-[12px] text-white/80 font-mono mt-0.5">{c.issued}</div>
          </div>
        </div>

        {/* Title */}
        <h3
          className="mt-6 text-[20px] md:text-[24px] leading-[1.2] tracking-tight text-white"
          style={{ fontFamily: "'Playfair Display', serif" }}
        >
          {c.title}
        </h3>

        {/* Issuer */}
        <div className="mt-3 flex items-start gap-2 text-[12.5px] text-white/55">
          <Building2 className="w-3.5 h-3.5 mt-0.5 shrink-0 text-white/35" />
          <span>{c.issuer}</span>
        </div>

        {/* Credential ID */}
        {c.credentialId && (
          <div className="mt-2 flex items-center gap-2 text-[11px] text-white/40 font-mono">
            <Hash className="w-3 h-3" />
            <span>ID · {c.credentialId}</span>
          </div>
        )}

        {/* Skills pills */}
        <div className="mt-5 flex flex-wrap gap-1.5">
          {c.skills.slice(0, 3).map((s) => (
            <span
              key={s}
              className="text-[10.5px] tracking-[0.12em] uppercase font-mono px-2.5 py-1 rounded-full border border-white/10 text-white/65 bg-white/[0.03]"
            >
              {s}
            </span>
          ))}
        </div>

        {/* CTA line */}
        <div className="mt-6 flex items-center justify-between">
          <span className="text-[11px] tracking-[0.3em] uppercase text-white/35 font-mono group-hover:text-white/70 transition-colors">
            View certificate
          </span>
          <ChevronRight className="w-4 h-4 text-white/35 group-hover:text-[#e6c769] group-hover:translate-x-1 transition-all" />
        </div>

        {/* Hover underline */}
        <div
          className="absolute left-0 right-0 bottom-0 h-px opacity-0 group-hover:opacity-100 transition-opacity"
          style={{ background: `linear-gradient(90deg, transparent, ${color}, transparent)` }}
        />
      </div>
    </motion.button>
  );
};

const Credentials = () => {
  const [open, setOpen] = React.useState(null);
  const [filter, setFilter] = React.useState("All");
  const items = useContent("credentials", CREDENTIALS);

  const list = React.useMemo(
    () => (filter === "All" ? items : items.filter((c) => c.category === filter)),
    [filter, items]
  );

  return (
    <section id="credentials" className="relative py-28 px-6 md:px-10">
      <div className="max-w-[1300px] mx-auto">
        {/* Heading */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mb-12"
        >
          <div className="text-[11px] tracking-[0.4em] uppercase text-[#e6c769]/80 font-mono">
            Credentials · Certificates · Programmes
          </div>
          <div className="mt-3 grid lg:grid-cols-2 gap-10 items-end">
            <h2
              className="text-[44px] md:text-[80px] leading-[0.95] tracking-tight text-white"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              The <span className="italic">paper</span> trail.
            </h2>
            <p className="text-white/55 text-[15px] leading-relaxed max-w-md justify-self-end">
              Every credential below is a deliberate stop along the way — Harvard, Ashoka, Bikaner Technical University, Great Learning, Coursera, Money Plant, GLS, 36ONE. Programmes attended, papers presented, skills earned.
            </p>
          </div>
        </motion.div>

        {/* Filter pills */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="flex flex-wrap gap-2 mb-10"
          data-testid="credentials-filters"
        >
          {FILTERS.map((f) => {
            const isActive = filter === f;
            const color = (CATEGORY_META[f] && CATEGORY_META[f].color) || "#e6c769";
            return (
              <button
                key={f}
                onClick={() => setFilter(f)}
                data-testid={`credentials-filter-${f.toLowerCase()}`}
                className={`text-[11px] tracking-[0.22em] uppercase font-mono px-4 py-2 rounded-full border transition-all ${
                  isActive
                    ? "text-black"
                    : "text-white/55 hover:text-white border-white/10 hover:border-white/25"
                }`}
                style={
                  isActive
                    ? { background: color, borderColor: color }
                    : undefined
                }
              >
                {f}
              </button>
            );
          })}
        </motion.div>

        {/* Stats strip */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-12"
        >
          {[
            { label: "Credentials", value: items.length.toString().padStart(2, "0"), Icon: ShieldCheck },
            { label: "Institutions", value: "10+", Icon: Building2 },
            { label: "Skills Earned", value: "30+", Icon: Sparkles },
            { label: "Years", value: "2023 — 26", Icon: Calendar },
          ].map((s, i) => (
            <div
              key={s.label}
              className="rounded-2xl border border-white/10 bg-white/[0.03] backdrop-blur-xl px-5 py-4 flex items-center gap-3"
              data-testid={`credentials-stat-${i}`}
            >
              <div className="w-10 h-10 rounded-xl grid place-items-center bg-[#e6c769]/10 text-[#e6c769] border border-[#e6c769]/25">
                <s.Icon className="w-4 h-4" />
              </div>
              <div>
                <div className="text-[10px] tracking-[0.3em] uppercase text-white/40 font-mono">{s.label}</div>
                <div className="text-[18px] text-white" style={{ fontFamily: "'Playfair Display', serif" }}>
                  {s.value}
                </div>
              </div>
            </div>
          ))}
        </motion.div>

        {/* Grid */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          <AnimatePresence mode="popLayout">
            {list.map((c, i) => (
              <CredentialRow key={c.id} c={c} onClick={() => setOpen(c)} index={i} />
            ))}
          </AnimatePresence>
        </div>
      </div>

      {/* Lightbox */}
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setOpen(null)}
            className="fixed inset-0 z-[100] bg-black/85 backdrop-blur-2xl flex items-center justify-center p-6"
            data-testid="credential-lightbox"
          >
            <motion.div
              initial={{ scale: 0.96, opacity: 0, y: 20 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.96, opacity: 0, y: 20 }}
              transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
              onClick={(e) => e.stopPropagation()}
              className="relative max-w-[760px] w-full max-h-[88vh] overflow-auto rounded-[24px] border border-white/10 bg-gradient-to-br from-[#0c0d12] to-[#06070b] p-8 md:p-10"
            >
              <div
                className="absolute -top-20 -right-20 w-80 h-80 rounded-full blur-3xl opacity-50"
                style={{ background: `${open.accent}30` }}
              />
              <button
                onClick={() => setOpen(null)}
                data-testid="credential-lightbox-close"
                className="absolute top-4 right-4 z-10 w-10 h-10 rounded-full border border-white/15 bg-white/5 hover:bg-white/10 grid place-items-center text-white"
                aria-label="Close"
              >
                <X className="w-5 h-5" />
              </button>

              <div className="relative">
                <div
                  className="text-[11px] tracking-[0.4em] uppercase font-mono"
                  style={{ color: open.accent }}
                >
                  {open.category} · {open.issued}
                </div>
                <h3
                  className="mt-3 text-[28px] md:text-[40px] leading-[1.08] tracking-tight text-white"
                  style={{ fontFamily: "'Playfair Display', serif" }}
                >
                  {open.title}
                </h3>

                <div className="mt-5 space-y-2.5 text-[13px] text-white/65">
                  <div className="flex items-center gap-2">
                    <Building2 className="w-4 h-4 text-white/40" />
                    {open.issuer}
                  </div>
                  <div className="flex items-center gap-2">
                    <Calendar className="w-4 h-4 text-white/40" />
                    Issued {open.issued}
                  </div>
                  {open.credentialId && (
                    <div className="flex items-center gap-2 font-mono">
                      <Hash className="w-4 h-4 text-white/40" />
                      Credential ID · {open.credentialId}
                    </div>
                  )}
                </div>

                <div className="mt-6 h-px bg-white/10" />

                <p className="mt-6 text-[15px] leading-[1.75] text-white/72">{open.detail}</p>

                <div className="mt-8">
                  <div className="text-[10px] tracking-[0.3em] uppercase text-white/40 font-mono mb-3">
                    Skills earned
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {(open.skills || []).map((s) => (
                      <span
                        key={s}
                        className="text-[11px] tracking-[0.14em] uppercase font-mono px-3 py-1.5 rounded-full border"
                        style={{
                          borderColor: `${open.accent}55`,
                          color: open.accent,
                          background: `${open.accent}10`,
                        }}
                      >
                        {s}
                      </span>
                    ))}
                  </div>
                </div>

                {open.certificate && (
                  <div className="mt-8">
                    <div className="text-[10px] tracking-[0.3em] uppercase text-white/40 font-mono mb-3">
                      Certificate
                    </div>
                    {/\.pdf(\?|$)/i.test(open.certificate) ? (
                      <a
                        href={resolveFileUrl(open.certificate)}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid={`credential-cert-link-${open.id}`}
                        className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full border border-white/15 text-white/80 text-[12px] tracking-[0.2em] uppercase font-mono hover:text-white hover:border-white/30 transition-colors"
                      >
                        <Hash className="w-3.5 h-3.5" />
                        View PDF
                      </a>
                    ) : (
                      <a
                        href={resolveFileUrl(open.certificate)}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid={`credential-cert-image-${open.id}`}
                        className="block rounded-xl overflow-hidden border border-white/10 hover:border-[#e6c769]/50 transition-colors"
                      >
                        <img
                          src={resolveFileUrl(open.certificate)}
                          alt={`${open.title} certificate`}
                          className="w-full h-auto"
                        />
                      </a>
                    )}
                  </div>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};

export default Credentials;
